import React, { useEffect, useState } from 'react';
import { Box, Title, Text, Card, Stack, Button, Anchor } from '@mantine/core';
import { IconLock } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react';
import { usePostLogout } from '../../global/entities/Login/mutations/usePostLogoutMutations';

const NoSeatsAvailable: React.FC = observer(() => {
    const { t } = useTranslation();
    const logoutMutation = usePostLogout();
    const [isLoggingOut, setIsLoggingOut] = useState(false);

    // Prevent navigation away from this page
    useEffect(() => {
        const handleBeforeUnload = () => {
            // Allow page refresh and close
            return undefined;
        };

        const handlePopState = () => {
            // Prevent browser back/forward navigation
            window.history.pushState(null, '', window.location.pathname);
        };

        // Push initial state to prevent back navigation
        window.history.pushState(null, '', window.location.pathname);
        
        window.addEventListener('beforeunload', handleBeforeUnload);
        window.addEventListener('popstate', handlePopState);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
            window.removeEventListener('popstate', handlePopState);
        };
    }, []);

    const handleLogout = async () => {
        setIsLoggingOut(true);
        try {
            await logoutMutation.mutateAsync();
        } catch (error) {
            console.error('Logout error:', error);
            // Fallback: clear session and redirect if API call fails
            localStorage.removeItem('currentUser');
            window.location.href = '/login';
        }
    };

    return (
        <>
            {/* Grey overlay that blocks all interactions */}
            <Box
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    backdropFilter: 'blur(4px)',
                    zIndex: 9998,
                    pointerEvents: 'all',
                }}
                onClick={(e) => e.preventDefault()}
            />
            
            {/* Modal card on top of overlay */}
            <Box
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 9999,
                    padding: '1rem',
                    pointerEvents: 'none',
                }}
            >
                <Card 
                    shadow="xl" 
                    padding="xl" 
                    radius="md" 
                    withBorder
                    style={{ 
                        maxWidth: '600px', 
                        width: '100%',
                        pointerEvents: 'all',
                    }}
                >
                    <Stack gap="lg" align="center">
                        <IconLock size={64} color="var(--mantine-color-orange-6)" stroke={1.5} />
                        
                        <Title order={1} ta="center" c="orange.7">
                            {t('noSeats.title', 'Clinic Seats Unavailable')}
                        </Title>

                        <Stack gap="md" w="100%">
                            <Text size="md" ta="center">
                                {t(
                                    'noSeats.description',
                                    'Your clinic subscription has reached its maximum capacity. To access the application, please contact your clinic to request additional seats or wait for a seat to become available.'
                                )}
                            </Text>

                            <Card withBorder padding="md" radius="sm" bg="gray.1" w="100%">
                                <Stack gap="xs">
                                    <Text size="sm" ta="center">
                                        {t('noSeats.contactAppMed', 'Contact AppMed at:')}
                                    </Text>
                                    <Text size="sm" ta="center">
                                        📧 <Anchor href={`mailto:${t('noSeats.emailAddress', 'info@appmed.ca')}`} c="blue">
                                            {t('noSeats.emailAddress', 'info@appmed.ca')}
                                        </Anchor>
                                    </Text>
                                    <Text size="sm" ta="center">{t('noSeats.phone', '📞 1-888-277-3660')}</Text>
                                </Stack>
                            </Card>
                        </Stack>

                        <Button 
                            size="md" 
                            color="orange"
                            onClick={handleLogout}
                            fullWidth
                            mt="md"
                            loading={isLoggingOut}
                            disabled={isLoggingOut}
                        >
                            {t('common.logout', 'Logout')}
                        </Button>
                    </Stack>
                </Card>
            </Box>
        </>
    );
});

export default NoSeatsAvailable;

